import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import SEO from "../../layout/SEO";
import NewsletterSubscribe from "../../layout/NewsletterSubscribe";
import publicApi from "../../../utils/publicApi";
import { isPublicArticle } from "../../../utils/articleVisibility";
import { useEditor, EditorContent } from '@tiptap/react';
import { createReadOnlyExtensions } from '../../../utils/tiptapReadOnly';
import 'katex/dist/katex.min.css';
import { useEffect } from 'react';
import { formatDate } from "../../../utils/dateUtils";
import {
    Github,
    Gitlab,
    Facebook,
    Twitter,
    Linkedin,
    Instagram,
    Youtube,
    Dribbble,
    PenTool,
    Code,
    MessageSquare,
    Globe,
    Link as LinkIcon,
    ArrowLeft,
    ArrowRight,
    BookOpen,
    FileText,
    ExternalLink
} from "lucide-react";
import BehanceIcon from '../../admin/icons/BehanceIcon';
import StatusScreen from '../../shared/StatusScreen';
import ArticleAudioPlayer from '../../shared/ArticleAudioPlayer';
import ShareButtons from '../../shared/ShareButtons';
import { getArticleTypeLabel } from '../../../utils/articleType';
import { attachImageFallback } from '../../../utils/imageFallback';

const DEFAULT_ARTICLE_COLOR = "#e45447";

const normalizeHexColor = (color = DEFAULT_ARTICLE_COLOR) => {
    const value = String(color || DEFAULT_ARTICLE_COLOR).trim();
    const hex = value.startsWith("#") ? value.slice(1) : value;

    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        return `#${hex.split("").map((char) => char + char).join("")}`;
    }

    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
        return `#${hex}`;
    }

    return DEFAULT_ARTICLE_COLOR;
};

const hexToRgb = (color) => {
    const normalizedColor = normalizeHexColor(color).slice(1);
    const value = parseInt(normalizedColor, 16);

    return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255
    };
};

const shadeHexColor = (color, amount) => {
    const { r, g, b } = hexToRgb(color);
    const clamp = (value) => Math.max(0, Math.min(255, value));
    const shaded = (clamp(r + amount) << 16) | (clamp(g + amount) << 8) | clamp(b + amount);

    return `#${shaded.toString(16).padStart(6, "0")}`;
};

const cssUrl = (url) => `url("${url.replace(/"/g, '\\"')}")`;

const parseSocialLinks = (value) => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value;
    }

    try {
        const parsedValue = JSON.parse(value);
        return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
        return [];
    }
};

const parseArticleContent = (content) => {
    if (!content) {
        return { type: "doc", content: [] };
    }

    if (typeof content === "object") {
        return content;
    }

    if (typeof content === "string" && content.trim() !== "") {
        try {
            return JSON.parse(content);
        } catch (error) {
            return {
                type: "doc",
                content: [{ type: "paragraph", content: [{ type: "text", text: content }] }]
            };
        }
    }

    return { type: "doc", content: [] };
};

const extractTextFromNode = (node) => {
    if (!node) {
        return "";
    }

    if (node.type === "text") {
        return node.text || "";
    }

    if (!Array.isArray(node.content)) {
        return "";
    }

    return node.content.map(extractTextFromNode).join("");
};

const slugifyAnchor = (value, fallback) => {
    const slug = String(value || fallback || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    return slug || fallback || "section";
};

const buildTableOfContents = (content) => {
    const doc = parseArticleContent(content);
    const items = [];
    const usedSlugs = new Map();

    const walk = (node) => {
        if (!node) {
            return;
        }

        const level = node.type === "heading" ? Number(node.attrs?.level || 0) : 0;
        if (level >= 2 && level <= 4) {
            const label = extractTextFromNode(node).trim();

            if (label) {
                const baseId = slugifyAnchor(label, `section-${items.length + 1}`);
                const count = usedSlugs.get(baseId) || 0;
                usedSlugs.set(baseId, count + 1);

                items.push({
                    id: count === 0 ? baseId : `${baseId}-${count + 1}`,
                    label,
                    level
                });
            }
        }

        if (Array.isArray(node.content)) {
            node.content.forEach(walk);
        }
    };

    walk(doc);
    return items;
};

// Composant pour afficher le contenu en lecture seule
const ReadOnlyEditor = ({ content, tocItems = [] }) => {
    const parsedContent = parseArticleContent(content);

    const editor = useEditor({
        extensions: createReadOnlyExtensions(),
        content: parsedContent || '',
        editable: false,
    });

    useEffect(() => {
        if (editor && content !== undefined) {
            const newContent = parseArticleContent(content);

            const currentContent = editor.getJSON();
            const currentContentStr = JSON.stringify(currentContent);
            const newContentStr = JSON.stringify(newContent || '');

            if (currentContentStr !== newContentStr) {
                editor.commands.setContent(newContent || '');
            }
        }
    }, [content, editor]);

    useEffect(() => {
        if (!editor || tocItems.length === 0) {
            return undefined;
        }

        const frame = window.requestAnimationFrame(() => {
            const headings = editor.view.dom.querySelectorAll("h2, h3, h4");

            tocItems.forEach((item, index) => {
                if (headings[index]) {
                    headings[index].setAttribute("id", item.id);
                }
            });
        });

        return () => window.cancelAnimationFrame(frame);
    }, [editor, tocItems]);

    useEffect(() => {
        if (!editor) {
            return undefined;
        }
        return attachImageFallback(editor.view.dom);
    }, [editor]);

    if (!editor) {
        return <div>Chargement du contenu...</div>;
    }

    return (
        <div className="tiptap-readonly">
            <EditorContent editor={editor} />
        </div>
    );
};

class ArticleDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            article: null,
            previousArticle: null,
            nextArticle: null,
            suggestedArticles: [],
            loading: true
        };
    }

    componentDidMount() {
        this.loadArticle();
    }

    componentDidUpdate(prevProps) {
        // Navigation d'un article à l'autre (« Article suivant », « Article
        // précédent », suggestions du bas de page). La route ne change pas,
        // seul le slug bouge : le composant n'est pas démonté, donc
        // componentDidMount ne se rejoue pas. Sans ça, l'URL changeait mais
        // l'article affiché restait le même.
        if (prevProps.match.params.slug !== this.props.match.params.slug) {
            this.setState({
                article: null,
                previousArticle: null,
                nextArticle: null,
                suggestedArticles: [],
                loading: true
            }, this.loadArticle);
        }
    }

    loadArticle = async () => {
        try {
            const { slug } = this.props.match.params;
            const [articleResult, articlesResult] = await Promise.allSettled([
                publicApi.get(`/api/articles/${slug}`),
                publicApi.get("/api/articles?limit=100")
            ]);

            const listResponse = articlesResult.status === "fulfilled" ? articlesResult.value : null;
            const articles = listResponse?.data?.articles || [];

            const articleResponse = articleResult.status === "fulfilled" ? articleResult.value : null;
            const loadedArticle = articleResponse?.data?.success && isPublicArticle(articleResponse.data.article)
                ? articleResponse.data.article
                : null;

            if (loadedArticle) {
                const { previousArticle, nextArticle } = this.getAdjacentArticles(loadedArticle, articles);

                this.setState({
                    article: loadedArticle,
                    previousArticle,
                    nextArticle,
                    suggestedArticles: this.buildSuggestions(articles, loadedArticle),
                    loading: false
                }, () => {
                    if (typeof window.resetScript === "function") {
                        window.resetScript();
                    }
                });
            } else {
                this.setState({
                    suggestedArticles: this.buildSuggestions(articles, null),
                    loading: false
                });
            }
        } catch (error) {
            console.error("Error loading article:", error);
            this.setState({ loading: false });
        }
    };

    buildSuggestions = (articles = [], currentArticle = null) => {
        return articles
            .filter(isPublicArticle)
            .filter((item) => (
                !currentArticle
                || (item.id !== currentArticle.id && item.slug !== currentArticle.slug)
            ))
            .sort((a, b) => {
                const dateA = new Date(a.publishedAt || a.createdAt || 0).getTime();
                const dateB = new Date(b.publishedAt || b.createdAt || 0).getTime();
                return dateB - dateA;
            })
            .slice(0, 3)
            .map((item) => ({
                to: `/article/${item.slug}`,
                type: this.getArticleTypeLabel(item.type),
                title: item.title,
                excerpt: item.excerpt
            }));
    };

    getAdjacentArticles = (currentArticle, articles = []) => {
        const articleExists = articles.some((article) => (
            article.id === currentArticle.id || article.slug === currentArticle.slug
        ));
        const completeList = articleExists ? articles : [...articles, currentArticle];

        const sortedArticles = completeList
            .filter(isPublicArticle)
            .sort((a, b) => {
                const dateA = new Date(a.publishedAt || a.createdAt || 0).getTime();
                const dateB = new Date(b.publishedAt || b.createdAt || 0).getTime();
                return dateB - dateA;
            });

        const currentIndex = sortedArticles.findIndex((article) => (
            article.id === currentArticle.id || article.slug === currentArticle.slug
        ));

        if (currentIndex === -1) {
            return { previousArticle: null, nextArticle: null };
        }

        return {
            previousArticle: sortedArticles[currentIndex + 1] || null,
            nextArticle: currentIndex > 0 ? sortedArticles[currentIndex - 1] : null
        };
    };

    render() {
        const { article, loading, previousArticle, nextArticle, suggestedArticles } = this.state;

        if (loading) {
            return (
                <StatusScreen
                    loading
                    seo={{ title: "Chargement de l'article…" }}
                    kicker="Un instant"
                    title={<>On va chercher <em>l'article</em>…</>}
                />
            );
        }

        if (!article) {
            return (
                <StatusScreen
                    seo={{
                        title: "Article introuvable",
                        description: "Le lien est erroné ou l'article n'est plus publié."
                    }}
                    kicker="Erreur 404"
                    title={<>Cet article<br />s'est <em>volatilisé</em>.</>}
                    lead="Lien cassé ou brouillon dépublié — ça arrive. Voici ce qui se lit le mieux en ce moment, à la place."
                    primary={{ to: "/article", label: "Voir tous les articles" }}
                    suggestions={suggestedArticles}
                />
            );
        }

        const color = normalizeHexColor(article.color || DEFAULT_ARTICLE_COLOR);
        const colorRgb = hexToRgb(color);
        const articleRgb = `${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}`;
        const hasImage = article.imageUrl && article.imageUrl.trim() !== "";
        const heroFallbackGradient = `linear-gradient(135deg, ${color} 0%, ${shadeHexColor(color, -48)} 100%)`;
        // Si l'image existe on la pose au-dessus du dégradé de secours :
        // image 404 => la couche url() est vide et le dégradé prend le relais.
        const heroImage = hasImage
            ? `${cssUrl(article.imageUrl)}, ${heroFallbackGradient}`
            : heroFallbackGradient;
        const articleTypeLabel = this.getArticleTypeLabel(article.type);
        const socialLinks = parseSocialLinks(article.socialLinks).filter((link) => link && link.url);
        const parsedContent = parseArticleContent(article.content);
        const tocItems = buildTableOfContents(parsedContent);
        const showToc = article.showToc !== false && tocItems.length > 0;
        const tocTitle = article.tocTitle?.trim() || "Dans l'article";
        const articleThemeStyle = {
            "--article-color": color,
            "--article-rgb": articleRgb,
            "--article-hero-image": heroImage
        };
        
        // Préparer les métadonnées SEO
        const seoTitle = article.metaTitle || article.title;
        const seoDescription = article.metaDescription || article.excerpt || `Article: ${article.title}`;
        const articleUrl = `${window.location.origin}/article/${article.slug}`;
        // Carte de partage 1200x630 générée par l'API à partir du titre et du
        // type. La couverture de l'article n'a pas le bon ratio et se fait
        // rogner n'importe comment par les réseaux.
        const ogImage = `${window.location.origin}/og/article/${encodeURIComponent(article.slug)}.png`;
        const authorName = article.author?.name || article.author?.email || '';
        const publishedTime = article.publishedAt ? new Date(article.publishedAt).toISOString() : null;
        const modifiedTime = article.updatedAt ? new Date(article.updatedAt).toISOString() : null;

        const articleJsonLd = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: seoTitle,
            description: seoDescription,
            image: ogImage,
            author: { "@type": "Person", name: authorName || "Ahmed Karim", url: window.location.origin },
            publisher: { "@type": "Person", name: "Ahmed Karim", url: window.location.origin },
            datePublished: publishedTime,
            dateModified: modifiedTime || publishedTime,
            mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
            inLanguage: "fr-FR"
        };

        return (
            <>
                <SEO
                    title={seoTitle}
                    description={seoDescription}
                    image={ogImage}
                    url={articleUrl}
                    type="article"
                    author={authorName}
                    keywords={`${article.title}, ${article.type?.toLowerCase() || 'article'}`}
                    publishedTime={publishedTime}
                    modifiedTime={modifiedTime}
                    jsonLd={articleJsonLd}
                />
                <Header modeDark={true} />

                <main className="article-detail-page" style={articleThemeStyle}>
                    <section className="article-detail-hero">
                        <div className="container">
                            <div className="article-detail-hero-content">
                                <p className="article-detail-crumbs">
                                    <Link to="/">Accueil</Link>
                                    <span>/</span>
                                    <Link to="/article">Articles</Link>
                                    <span>/</span>
                                    <Link to={window.location.pathname}>{article.title}</Link>
                                </p>

                                <div className="article-detail-hero-tags">
                                    <span className="article-detail-kicker">{articleTypeLabel}</span>
                                    {article.featured && (
                                        <span className="article-featured-badge">
                                            <i className="lnr lnr-star"></i> À la une
                                        </span>
                                    )}
                                </div>

                                <h1 className="article-detail-title">{article.title}</h1>
                                {article.excerpt && (
                                    <p className="article-detail-excerpt">{article.excerpt}</p>
                                )}
                                <div className="article-detail-meta">
                                    {article.author && (
                                        <span>
                                            <strong>{article.author.name || article.author.email}</strong>
                                        </span>
                                    )}
                                    {article.publishedAt && (
                                        <span>{formatDate(article.publishedAt)}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="article-detail-body">
                        {article.audioEnabled !== false && (
                            <div className="container article-audio-wrap">
                                <ArticleAudioPlayer title={article.title} />
                            </div>
                        )}
                        <div className={`container article-detail-layout ${showToc ? "" : "article-detail-layout--solo"}`}>
                            <div className="article-detail-main-column">
                                <article className="article-detail-container article-detail-paper">
                                    <ShareButtons title={article.title} />
                                    <div className="article-detail-content" id="article-content">
                                        <ReadOnlyEditor content={parsedContent} tocItems={tocItems} />
                                    </div>

                                    {socialLinks.length > 0 && (
                                        <div className="article-source-links" id="article-sources">
                                            <div className="article-source-header">
                                                <span>Annexes</span>
                                                <h3>Liens et sources</h3>
                                            </div>
                                            <div className="source-links-grid">
                                                {socialLinks.map((link, index) => {
                                                    const fallbackLabel = this.getSocialLinkLabel(link.type);
                                                    const label = link.label?.trim() || fallbackLabel;
                                                    const description = link.description?.trim() || this.getDomainFromUrl(link.url);

                                                    return (
                                                        <a
                                                            key={index}
                                                            href={link.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="source-link-card"
                                                            title={label}
                                                        >
                                                            <div className="source-link-icon">
                                                                {this.getSocialLinkIcon(link.type)}
                                                            </div>
                                                            <div className="source-link-info">
                                                                <span className="source-link-type">{fallbackLabel}</span>
                                                                <strong>{label}</strong>
                                                                <span>{description}</span>
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </article>

                                {(previousArticle || nextArticle) && (
                                    <nav className="article-adjacent-nav" aria-label="Navigation entre les articles">
                                        {previousArticle ? (
                                            <Link to={`/article/${previousArticle.slug}`} className="article-adjacent-card article-adjacent-card--previous">
                                                <span className="article-adjacent-direction">
                                                    <ArrowLeft size={17} />
                                                    Article précédent
                                                </span>
                                                <strong>{previousArticle.title}</strong>
                                                {previousArticle.excerpt && <span>{previousArticle.excerpt}</span>}
                                            </Link>
                                        ) : <span className="article-adjacent-placeholder" aria-hidden="true"></span>}

                                        {nextArticle ? (
                                            <Link to={`/article/${nextArticle.slug}`} className="article-adjacent-card article-adjacent-card--next">
                                                <span className="article-adjacent-direction">
                                                    Article suivant
                                                    <ArrowRight size={17} />
                                                </span>
                                                <strong>{nextArticle.title}</strong>
                                                {nextArticle.excerpt && <span>{nextArticle.excerpt}</span>}
                                            </Link>
                                        ) : <span className="article-adjacent-placeholder" aria-hidden="true"></span>}
                                    </nav>
                                )}

                                <div id="article-newsletter">
                                    <NewsletterSubscribe variant="article" />
                                </div>
                            </div>

                            {showToc && (
                                <aside className="article-detail-toc" aria-label="Sommaire de l'article">
                                    <h2>{tocTitle}</h2>
                                    <nav>
                                        {tocItems.map((item) => (
                                            <a
                                                key={item.id}
                                                className={`toc-link toc-link-level-${item.level}`}
                                                href={`#${item.id}`}
                                            >
                                                {item.label}
                                            </a>
                                        ))}
                                        {socialLinks.length > 0 && (
                                            <a className="toc-link toc-link--meta" href="#article-sources">
                                                Liens et sources
                                            </a>
                                        )}
                                        <a className="toc-link toc-link--meta" href="#article-newsletter">
                                            Newsletter
                                        </a>
                                    </nav>
                                </aside>
                            )}
                        </div>
                    </section>
                </main>

                <Footer />
            </>
        );
    }

    getArticleTypeLabel = (type) => getArticleTypeLabel(type);

    getSocialLinkIcon = (type) => {
        const iconProps = { size: 24 };

        switch (type) {
            case "source": return <FileText {...iconProps} />;
            case "documentation": return <BookOpen {...iconProps} />;
            case "demo": return <ExternalLink {...iconProps} />;
            case "github": return <Github {...iconProps} />;
            case "gitlab": return <Gitlab {...iconProps} />;
            case "facebook": return <Facebook {...iconProps} />;
            case "twitter": return <Twitter {...iconProps} />;
            case "linkedin": return <Linkedin {...iconProps} />;
            case "instagram": return <Instagram {...iconProps} />;
            case "youtube": return <Youtube {...iconProps} />;
            case "dribbble": return <Dribbble {...iconProps} />;
            case "behance": return <BehanceIcon size={24} />;
            case "medium": return <PenTool {...iconProps} />;
            case "devto": return <Code {...iconProps} />;
            case "codepen": return <Code {...iconProps} />;
            case "stackoverflow": return <MessageSquare {...iconProps} />;
            case "reddit": return <MessageSquare {...iconProps} />;
            case "discord": return <MessageSquare {...iconProps} />;
            case "telegram": return <MessageSquare {...iconProps} />;
            case "whatsapp": return <MessageSquare {...iconProps} />;
            case "mastodon": return <MessageSquare {...iconProps} />;
            case "website": return <Globe {...iconProps} />;
            default: return <LinkIcon {...iconProps} />;
        }
    };

    getSocialLinkLabel = (type) => {
        const labels = {
            "source": "Source",
            "documentation": "Documentation",
            "demo": "Démo",
            "link": "Lien générique",
            "github": "GitHub",
            "gitlab": "GitLab",
            "facebook": "Facebook",
            "twitter": "Twitter",
            "linkedin": "LinkedIn",
            "instagram": "Instagram",
            "youtube": "YouTube",
            "dribbble": "Dribbble",
            "behance": "Behance",
            "medium": "Medium",
            "devto": "Dev.to",
            "codepen": "CodePen",
            "stackoverflow": "Stack Overflow",
            "reddit": "Reddit",
            "discord": "Discord",
            "telegram": "Telegram",
            "whatsapp": "WhatsApp",
            "mastodon": "Mastodon",
            "website": "Site web"
        };
        return labels[type] || "Lien générique";
    };

    getDomainFromUrl = (url) => {
        try {
            const domain = new URL(url).hostname;
            return domain.replace('www.', '');
        } catch (e) {
            return url;
        }
    };
}

export default withRouter(ArticleDetail);
